import { create } from "zustand";

interface SlugTranslations {
  fr: string;
  en: string;
}

interface LanguageSwitcherState {
  /**
   * Map of slug -> translations
   * Example: { "mon-article": { fr: "mon-article", en: "my-article" }, "my-article": { fr: "mon-article", en: "my-article" } }
   */
  slugs: Record<string, SlugTranslations>;
  /**
   * Add a slug translation to the slugs map.
   *
   * @example addSlugTranslation({ fr: "mon-article", en: "my-article" }) => { "mon-article": { fr: "mon-article", en: "my-article" }, "my-article": { fr: "mon-article", en: "my-article" } }
   * @param translations
   */
  addSlugTranslation: (translations: SlugTranslations) => void;
  /**
   * Recursive method to sync slugs from data.
   *
   * It will look for any slug pairs that starts with slug and ends with Fr or En (slugFr/slugEn, slugChampionFr/slugChampionEn, etc.)
   * If it finds a slug pair, it will add it to the slugs map.
   * If it finds an object, it will call itself recursively.
   *
   * @param data
   * @returns
   */
  syncSlugsFromData: (data: any) => void;
  /**
   * Translate url params from a params object.
   * Takes the slug found in the url and looks in the stored slugs if it exists, and returns the translated slug.
   *
   * @example getTranslatedParams({ slug: "mon-article", slugChampion: "mon-champion" }, "en") => { slug: "my-article", slugChampion: "my-champion" }
   * @param params
   * @param lang
   * @returns
   */
  getTranslatedParams: (
    params: Record<string, string | undefined>,
    lang: string,
  ) => Record<string, string | undefined>;
  /**
   * Clear the slugs map.
   */
  clearSlugs: () => void;
}

const useLanguageSwitcherStore = create<LanguageSwitcherState>((set, get) => ({
  slugs: {},
  addSlugTranslation: (translations) =>
    set((state) => ({
      slugs: {
        ...state.slugs,
        [translations.fr]: translations,
        [translations.en]: translations,
      },
    })),
  syncSlugsFromData: (data) => {
    if (!data || typeof data !== "object") return;

    if (Array.isArray(data)) {
      data.forEach(get().syncSlugsFromData);
      return;
    }

    // Capture any slug pairs that starts with slug and ends with Fr or En (slugFr/slugEn, slugChampionFr/slugChampionEn, etc.),
    Object.entries(data).forEach(([key, value]) => {
      if (key.startsWith("slug") && key.endsWith("Fr")) {
        // Get the English slug, e.g. slugChampionFr -> slugChampionEn
        const enKey = key.replace("Fr", "En");
        const enValue = data[enKey];

        // If we have both the French and English slugs, add them to the slugs map
        if (value && enValue) {
          get().addSlugTranslation({
            fr: value as string,
            en: enValue as string,
          });
        }
      } else if (value && typeof value === "object") {
        get().syncSlugsFromData(value);
      }
    });
  },

  getTranslatedParams: (params, lang) => {
    const { slugs } = get();
    const updatedParams = { ...params };

    for (const key in params) {
      if (key.toLowerCase().startsWith("slug")) {
        const currentValue = params[key];
        if (currentValue && slugs[currentValue]) {
          updatedParams[key] =
            slugs[currentValue][lang as keyof SlugTranslations];
        }
      }
    }

    return updatedParams;
  },

  clearSlugs: () => set({ slugs: {} }),
}));

export default useLanguageSwitcherStore;
